'use client';

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import {
  Heart,
  Loader2,
  CreditCard,
  ArrowLeft,
  Lock,
  Check,
  AlertCircle,
  CheckCircle2,
  FileDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input/input';
import { Label } from '@/components/ui/label/label';
import { Textarea } from '@/components/ui/textarea/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog/dialog';
import { Skeleton } from '@/components/ui/skeleton/skeleton';
import { Switch } from '@/components/ui/switch/switch';
import { DonorAddressFields, TAX_NUMBER_COUNTRIES } from './DonorAddressFields';
import { GiftAidSection } from './GiftAidSection';
import { trpc } from '@/lib/trpc';
import { isTurnstileRejection } from '@/lib/turnstile';
import { useTenantStore } from '@/stores/tenant-store';
import { useDonationImpactMessage } from '@/hooks/use-donation-impact-message';
import { getTenantConfig } from '@/tenants/config';
import { type DonationStrategy } from '@/tenants/_shared/hooks/donation-strategies';
import {
  saveAdyenRedirectSession,
  loadAdyenRedirectSession,
  clearAdyenRedirectSession,
  saveInFlightPayment,
  clearInFlightPayment,
} from '@/tenants/_shared/lib/adyenRedirectSession';
import { saveStripeCheckoutPayload } from '@/tenants/_shared/lib/stripeCheckoutHandoff';
import { useSessionReplay } from '@/tenants/_shared/lib/session-replay';
import { useRegional } from '@/components/providers/regional-provider';
import { getCountryFromCurrency, buildLocalePath, type Locale } from '@/i18n/config';
import type { PaymentCompletedData, PaymentFailedData } from '@adyen/adyen-web';
import { type CheckoutSessionResult } from '@impact/types';
import { SETTLED_DONATION_STATUSES } from '@impact/types/enums';
import { AmountSelector } from './AmountSelector';
import { trackDonation } from '@/tenants/_shared/lib/donation-analytics';
import { useSessionRecovery } from '@/tenants/_shared/hooks/useSessionRecovery';
import { usePaymentRedirectResolution } from '@/tenants/_shared/hooks/usePaymentRedirectResolution';
import {
  getMissingAddressFields,
  type AddressValidationField,
} from '@/tenants/_shared/lib/address-validation';
import { isDonorEmailValid, normalizeDonorEmail } from '@/tenants/_shared/lib/donor-email';
import { useDeviceCapabilities } from '@/hooks/use-device-capabilities';
import { useScrollIntoViewOnReveal } from '@/hooks/use-scroll-into-view-on-reveal';
import { cn } from '@/lib/utils';

const AdyenCheckout = dynamic(
  () =>
    import('@/tenants/_shared/components/AdyenCheckout').then((m) => ({
      default: m.AdyenCheckout,
    })),
  { ssr: false },
);

// Eagerly prefetch Adyen chunk + preconnect to Adyen hosts so the Drop-in
// renders instantly when "Continue to Payment" is clicked.
if (typeof window !== 'undefined') {
  void import('@/tenants/_shared/components/AdyenCheckout');

  const adyenEnv = process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT === 'live' ? 'live' : 'test';
  for (const host of [
    `https://checkoutshopper-${adyenEnv}.adyen.com`,
    `https://checkoutshopper-${adyenEnv}.cdn.adyen.com`,
  ]) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = host;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
}

const PayPalCheckout = dynamic(
  () =>
    import('@/tenants/_shared/components/PayPalCheckout').then((m) => ({
      default: m.PayPalCheckout,
    })),
  { ssr: false },
);

// Address structure
interface Address {
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Default donation amounts
const defaultDonationAmounts = [25, 50, 100, 250];

export interface DonationFormModalProps {
  /** Whether the modal is open (controlled) */
  open: boolean;
  /** Callback when the modal open state changes (controlled) */
  onOpenChange: (open: boolean) => void;
  /** Initial amount: number > 0 skips to details step, null shows amount step */
  initialAmount: number | null;
  recipientName: string;
  /** Donation strategy — provides payment config, currency, and session creation */
  strategy: DonationStrategy;
  donationAmounts?: number[];
  onSubmit?: (data: DonationData) => void;
  /** Donation ID from redirect return (auto-opens modal in finalizing state) */
  returnDonationId?: string;
  /** Adyen redirectResult from bank redirect return */
  redirectResult?: string | null;
  /** Adyen sessionId from redirect return URL */
  returnSessionId?: string | null;
  /** Called when a redirect payment is confirmed paid (use to invalidate/refetch totals) */
  onRedirectSuccess?: () => void;
  /** Recovery token from redirect return URL for IAB session restoration */
  recoveryToken?: string | null;
  /** Whether this is a localStorage recovery (native app 3DS return with no URL params) */
  isLocalStorageRecovery?: boolean;
  /** Visual theme override. Auto-resolves from tenant slug if omitted (DGD → light). */
  theme?: 'light' | 'dark';
}

export interface DonationData {
  amount: number;
  name: string;
  email: string;
  message?: string;
  anonymous: boolean;
  address: Address;
  donorTaxNumber?: string;
  donationId?: string;
}

type FormStep =
  | 'amount'
  | 'details'
  | 'payment'
  | 'processing'
  | 'voucher-pending'
  | 'success'
  | 'finalizing'
  | 'awaiting-settlement'
  | 'failed';

/** Resolved state for redirect finalization */
interface FinalizingState {
  donationId: string;
  redirectResult: string;
  sessionId: string;
  sessionData: string;
}

// --- Grouped state interfaces ---

interface AmountState {
  selected: number | null;
  custom: string;
}

interface DonorState {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  anonymous: boolean;
  taxNumber: string;
  donationType: 'individual' | 'organization';
  companyName: string;
  displayName: string;
  giftAidEligible: boolean;
}

interface AddressState {
  fields: Address;
}

interface PaymentState {
  checkoutSession: CheckoutSessionResult | null;
  error: string | null;
  emailError: string | null;
  adyenReady: boolean;
}

interface RedirectState {
  optimisticSuccess: boolean;
  finalizing: FinalizingState | null;
}

// --- Factory functions ---

const INITIAL_ADDRESS: Address = {
  streetAddress: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
};

function createInitialAmount(amounts: number[]): AmountState {
  return { selected: amounts[1] || 50, custom: '' };
}

function createInitialDonor(): DonorState {
  return {
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    anonymous: false,
    taxNumber: '',
    donationType: 'individual',
    companyName: '',
    displayName: '',
    giftAidEligible: false,
  };
}

function resolveDonorName(donor: DonorState, fallbackEmail: string) {
  if (donor.donationType === 'organization') {
    return {
      firstName: donor.companyName.trim() || fallbackEmail.split('@')[0],
      lastName: '-',
    };
  }
  return {
    firstName: donor.firstName.trim() || fallbackEmail.split('@')[0],
    lastName: donor.lastName.trim() || '-',
  };
}

function createInitialAddress(): AddressState {
  return { fields: { ...INITIAL_ADDRESS } };
}

function createInitialPayment(): PaymentState {
  return { checkoutSession: null, error: null, emailError: null, adyenReady: false };
}

function createInitialRedirect(): RedirectState {
  return { optimisticSuccess: false, finalizing: null };
}

export function DonationFormModal({
  open,
  onOpenChange,
  initialAmount,
  recipientName,
  strategy,
  donationAmounts = defaultDonationAmounts,
  onSubmit,
  returnDonationId,
  redirectResult,
  returnSessionId,
  onRedirectSuccess,
  recoveryToken: recoveryTokenProp,
  isLocalStorageRecovery = false,
  theme,
}: DonationFormModalProps) {
  const locale = useLocale();
  const router = useRouter();
  const tForm = useTranslations('donate.form');
  const tCommon = useTranslations('common');
  const impactMessage = useDonationImpactMessage();
  const { isMobile } = useDeviceCapabilities();
  const detailsValidationRef = useRef<HTMLDivElement>(null);
  const stepEnteredAtRef = useRef<number>(Date.now());

  // Capture the donation → checkout journey when replay is enabled (env, per
  // tenant). Starts on open; recording persists through the /donate/checkout
  // handoff so a stuck Payment Element is visible.
  useSessionReplay(open);

  // Payment config + currency from strategy
  const {
    paymentConfig: paymentConfigData,
    isPaymentConfigLoading,
    isPaymentConfigFetching,
    isPaymentConfigError,
    currency,
  } = strategy;

  // Analytics context — device + currency on every event
  const analyticsCtx = useMemo(
    () => ({
      device: (isMobile ? 'mobile' : 'desktop') as 'mobile' | 'desktop',
      currency: currency.code,
    }),
    [isMobile, currency.code],
  );

  // Geo-detected country for Adyen payment method selection
  const { detectedCountry } = useRegional();
  const donorCountryCode = detectedCountry || getCountryFromCurrency(currency.code);

  // Tenant slug for receipt URL
  const tenantSlug = useTenantStore((s) => s.slug);

  // Theme — auto-resolves per tenant config (`donationTheme: 'light' | 'dark'`)
  // so DGD, UWR, and any future light-mode tenant share the same surface.
  const resolvedTheme = theme ?? getTenantConfig(tenantSlug).donationTheme ?? 'dark';
  const themeLightAttr = resolvedTheme === 'light' ? 'true' : undefined;

  // All countries for address dropdown
  const emptyCountries: Array<{ id: string; code: string; name: string }> = [];
  const allCountriesQuery = trpc.publicLookups.allCountries.useQuery();
  const allCountries = allCountriesQuery.data ?? emptyCountries;

  // Pre-fill from logged-in entrant's profile (fails silently for unauthenticated users)
  const entrantProfile = trpc.myProfile.getProfile.useQuery(undefined, {
    retry: false,
    staleTime: Infinity,
  });

  // Platform fee
  const [includePlatformFee, setIncludePlatformFee] = useState(true);
  const { data: feeConfig } = trpc.publicDonation.getPlatformFeeCap.useQuery(
    { currencyCode: currency.code },
    { staleTime: 5 * 60 * 1000 },
  );

  // Shared mutations (not strategy-specific)
  const capturePayPalOrder = trpc.publicDonation.capturePayPalOrder.useMutation();
  const createCheckoutSession = trpc.publicDonation.createCheckoutSession.useMutation();
  const commitCheckout = trpc.publicDonation.commitCheckout.useMutation();
  const markRedirectFailed = trpc.publicDonation.markRedirectFailed.useMutation();

  // --- Step ---
  const [currentStep, setCurrentStep] = useState<FormStep>('amount');

  // --- Amount ---
  const [amount, setAmount] = useState<AmountState>(() => createInitialAmount(donationAmounts));

  // --- Donor ---
  // Read redirect session once on mount (before effect clears it)
  const [redirectDisplayAmount] = useState<{ amount: number; currencySymbol: string } | null>(
    () => {
      if (!returnSessionId) return null;
      const stored = loadAdyenRedirectSession(returnSessionId);
      if (!stored?.amount) return null;
      return { amount: stored.amount, currencySymbol: stored.currencySymbol };
    },
  );
  const [donor, setDonor] = useState<DonorState>(() => {
    const initial = createInitialDonor();
    if (!returnSessionId) return initial;
    const stored = loadAdyenRedirectSession(returnSessionId);
    if (!stored) return initial;
    return { ...initial, email: stored.donorEmail };
  });

  // --- Address ---
  const [address, setAddress] = useState<AddressState>(createInitialAddress);

  // --- Self-attribution ---
  const [selfAttributeEnabled, setSelfAttributeEnabled] = useState(true);

  const effectiveCheckoutScope = useMemo(() => {
    const base = strategy.checkoutScope;
    if (
      (base.scope === 'general' || base.scope === 'event') &&
      selfAttributeEnabled &&
      strategy.selfAttribution
    ) {
      return { ...base, selfAttributionEntrantId: strategy.selfAttribution.entrantId };
    }
    return base;
  }, [strategy.checkoutScope, selfAttributeEnabled, strategy.selfAttribution]);

  // --- Payment ---
  const [payment, setPayment] = useState<PaymentState>(createInitialPayment);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [showDetailsValidation, setShowDetailsValidation] = useState(false);
  const [showDisplayName, setShowDisplayName] = useState(false);

  // --- Redirect ---
  const [redirect, setRedirect] = useState<RedirectState>(createInitialRedirect);

  // Pre-fill donor name, email, and address from entrant profile
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (prefillApplied.current || !entrantProfile.data) return;
    const p = entrantProfile.data;
    prefillApplied.current = true;

    setDonor((prev) => ({
      ...prev,
      firstName: prev.firstName || p.firstName || '',
      lastName: prev.lastName || p.lastName || '',
      email: prev.email || p.email || '',
    }));

    setAddress((prev) => {
      const f = prev.fields;
      const hasExisting = f.streetAddress || f.city || f.postalCode;
      if (hasExisting) return prev;
      return {
        ...prev,
        fields: {
          streetAddress: p.addressLine1 || '',
          addressLine2: p.addressLine2 || '',
          city: p.city || '',
          state: p.stateCode || '',
          postalCode: p.postalCode || '',
          country: p.countryCode || f.country,
        },
      };
    });
  }, [entrantProfile.data]);

  // --- Partial-update helpers (for JSX event handlers) ---
  const updateAddressField = <K extends keyof Address>(field: K, value: Address[K]) =>
    setAddress((prev) => ({ ...prev, fields: { ...prev.fields, [field]: value } }));

  const updateDonor = <K extends keyof DonorState>(field: K, value: DonorState[K]) =>
    setDonor((prev) => ({ ...prev, [field]: value }));

  const updatePayment = <K extends keyof PaymentState>(field: K, value: PaymentState[K]) =>
    setPayment((prev) => ({ ...prev, [field]: value }));

  // IAB session recovery: restore auth session after bank redirect lands in native browser
  const isAdyenRedirectReturn = !!redirectResult && !!returnDonationId;
  const isRedirectReturn = isAdyenRedirectReturn || (isLocalStorageRecovery && !!returnDonationId);
  const recovery = useSessionRecovery({
    recoveryToken: recoveryTokenProp ?? null,
    isRedirectReturn,
    isAuthenticated: !!entrantProfile.data,
  });

  useEffect(() => {
    if (!recoveryTokenProp || !recovery.attempted) return;
    trackDonation('donation_session_recovery', {
      outcome: recovery.recovered ? 'succeeded' : 'failed',
      donation_id: returnDonationId,
    });
  }, [recoveryTokenProp, recovery.attempted, recovery.recovered, returnDonationId]);

  // Read stored redirect session once on mount (before effects clear it)
  const storedRedirectSession = useMemo(
    () => (returnSessionId ? loadAdyenRedirectSession(returnSessionId) : null),
    [returnSessionId],
  );

  // Redirect context — webhook race check + DB session fallback + donorEmail
  const {
    data: redirectContext,
    isError: isRedirectContextError,
    refetch: refetchRedirectContext,
  } = trpc.publicDonation.getRedirectContext.useQuery(
    { donationId: returnDonationId! },
    { enabled: isRedirectReturn },
  );

  // Shared redirect resolution hook — settlement polling, guards, timeouts
  const {
    resolution,
    redirectSession: resolvedRedirectSession,
    settlement,
    finalizingTimedOut,
    pollInterval,
    dismissFinalizingTimeout,
  } = usePaymentRedirectResolution({
    isRedirectReturn,
    donationId: returnDonationId ?? null,
    redirectResult: redirectResult ?? null,
    returnSessionId: returnSessionId ?? null,
    redirectContext: redirectContext
      ? {
          status: redirectContext.status,
          sessionData: redirectContext.sessionData,
          sessionId: redirectContext.sessionId,
        }
      : null,
    isRedirectContextError,
    storedSessionData: storedRedirectSession?.sessionData ?? null,
    recoveryAttempted: recoveryTokenProp ? recovery.attempted : true,
  });

  // Settlement polling — hook drives interval via `pollInterval`, we refetch manually
  useEffect(() => {
    if (!pollInterval) return;
    const interval = setInterval(() => {
      void refetchRedirectContext();
    }, pollInterval as number);
    return () => clearInterval(interval);
  }, [pollInterval, refetchRedirectContext]);

  // localStorage recovery polling — polls for settlement when the normal resolution hook
  // can't run (no redirectResult). Stops when redirectContext shows a settled status.
  useEffect(() => {
    if (!isLocalStorageRecovery || currentStep !== 'awaiting-settlement') return;
    const interval = setInterval(() => {
      void refetchRedirectContext();
    }, 3_000);
    return () => clearInterval(interval);
  }, [isLocalStorageRecovery, currentStep, refetchRedirectContext]);

  // localStorage recovery — react to redirectContext updates during polling
  useEffect(() => {
    if (!isLocalStorageRecovery || currentStep !== 'awaiting-settlement' || !redirectContext)
      return;

    const status = redirectContext.status;
    if (!(SETTLED_DONATION_STATUSES as readonly string[]).includes(status)) return;

    clearInFlightPayment();
    if (status === 'paid') {
      trackDonation('donation_payment_completed', {
        result_code: 'Authorised',
        donation_id: returnDonationId,
        payment_method: 'redirect',
        event_source: 'webhook_settled',
      });
      onRedirectSuccessRef.current?.();
      setCurrentStep('success');
    } else {
      setCurrentStep('failed');
    }
  }, [isLocalStorageRecovery, currentStep, redirectContext, returnDonationId]);

  const effectiveDonorEmail = donor.email || redirectContext?.donorEmail || '';
  const normalizedDonorEmail = normalizeDonorEmail(effectiveDonorEmail);

  // Receipt availability — query getStatus on success step to check if tax receipt is available
  const successDonationId = payment.checkoutSession?.donationId ?? returnDonationId;
  const isReceiptPollEnabled = currentStep === 'success' && !!successDonationId;
  const receiptPollStartRef = useRef<number | null>(null);

  if (isReceiptPollEnabled && !receiptPollStartRef.current) {
    receiptPollStartRef.current = Date.now();
  }

  const RECEIPT_POLL_INTERVAL_MS = 2000;
  const RECEIPT_POLL_MAX_MS = 15_000;

  const { data: receiptStatus } = trpc.publicDonation.getStatus.useQuery(
    { donationId: successDonationId! },
    {
      enabled: isReceiptPollEnabled,
      refetchInterval: (query) => {
        if (query.state.data?.taxReceiptAvailable) return false;
        const elapsed = Date.now() - (receiptPollStartRef.current ?? Date.now());
        if (elapsed >= RECEIPT_POLL_MAX_MS) return false;
        return RECEIPT_POLL_INTERVAL_MS;
      },
    },
  );

  // Track last payment method type from Adyen for analytics
  const lastPaymentMethodRef = useRef<string>('card');
  // Track PayPal donation ID separately (lives inside PayPalCheckout's sessionRef, not in checkoutSession)
  const paypalDonationIdRef = useRef<string | null>(null);

  // Track previous open state for detecting open transitions
  const prevOpenRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  const onRedirectSuccessRef = useRef(onRedirectSuccess);
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    onRedirectSuccessRef.current = onRedirectSuccess;
  }, [onRedirectSuccess]);

  // Auto-fill donor name/email from self-attribution context
  useEffect(() => {
    const attr = strategy.selfAttribution;
    if (selfAttributeEnabled && attr) {
      setDonor((prev) => ({
        ...prev,
        firstName: prev.firstName || attr.firstName,
        lastName: prev.lastName || attr.lastName,
        email: prev.email || attr.email,
      }));
    }
  }, [selfAttributeEnabled, strategy.selfAttribution]);

  useEffect(() => {
    if (!open || !donorCountryCode || address.fields.country) {
      return;
    }

    setAddress((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        country: donorCountryCode,
      },
    }));
  }, [address.fields.country, donorCountryCode, open]);

  // Handle step initialization when modal opens
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open || wasOpen) return; // Only run on open transition (false → true)
    if (isRedirectReturn) return; // Redirect effect handles its own step

    trackDonation('donation_modal_opened', {
      scope: strategy.checkoutScope.scope,
      recipient_name: recipientName,
      initial_amount: initialAmount,
      is_redirect_return: false,
    });

    queueMicrotask(() => {
      if (initialAmount != null && initialAmount > 0) {
        setAmount((prev) => ({ ...prev, selected: initialAmount, custom: '' }));
        setCurrentStep('details');
      } else {
        setCurrentStep('amount');
      }
    });
  }, [open, initialAmount, isRedirectReturn]);

  // Auto-open modal on redirect return — enter finalizing step
  useEffect(() => {
    if (!isRedirectReturn || !returnDonationId) return;
    if (!redirectResult && !isLocalStorageRecovery) return;
    if (autoOpenedRef.current) return;

    autoOpenedRef.current = true;

    trackDonation('donation_modal_opened', {
      scope: strategy.checkoutScope.scope,
      recipient_name: recipientName,
      initial_amount: null,
      is_redirect_return: true,
    });

    queueMicrotask(() => {
      setCurrentStep('finalizing');
      onOpenChangeRef.current(true);
    });
  }, [isRedirectReturn, returnDonationId, redirectResult, isLocalStorageRecovery]);

  // Destructure for effect dep — avoids re-runs from unrelated redirect field changes
  const { finalizing: finalizingState } = redirect;

  // Resolution action effect — acts on shared hook's resolution output
  const prevResolutionRef = useRef<typeof resolution>(null);
  useEffect(() => {
    if (!resolution || !returnDonationId || resolution === prevResolutionRef.current) return;
    prevResolutionRef.current = resolution;

    switch (resolution.kind) {
      case 'already-settled':
        queueMicrotask(() => {
          trackDonation('donation_redirect_resumed', {
            donation_id: returnDonationId,
            webhook_settled: true,
            has_stored_session: !!storedRedirectSession,
          });
          if (resolution.status === 'paid') {
            trackDonation('donation_payment_completed', {
              result_code: 'Authorised',
              donation_id: returnDonationId,
              payment_method: 'redirect',
              event_source: 'webhook_settled',
            });
            trackDonation('donation_completed', {
              donation_id: returnDonationId,
              amount: storedRedirectSession?.amount ?? 0,
              currency: currency.code,
              event_source: 'webhook_settled',
            });
            onRedirectSuccessRef.current?.();
            setCurrentStep('success');
          } else {
            trackDonation('donation_redirect_failed', {
              donation_id: returnDonationId,
              reason: resolution.status,
            });
            setCurrentStep('failed');
          }
        });
        break;

      case 'finalize-payment':
        // Clean up sessionStorage now that the hook captured the data
        if (returnSessionId) clearAdyenRedirectSession(returnSessionId);
        trackDonation('donation_redirect_resumed', {
          donation_id: returnDonationId,
          webhook_settled: false,
          has_stored_session: !!storedRedirectSession,
        });
        setRedirect((prev) => ({ ...prev, finalizing: resolvedRedirectSession }));
        break;

      case 'awaiting-settlement':
        queueMicrotask(() => {
          setCurrentStep('awaiting-settlement');
        });
        break;

      case 'failed':
        queueMicrotask(() => {
          trackDonation('donation_redirect_failed', {
            donation_id: returnDonationId,
            reason: resolution.status,
          });
          setCurrentStep('failed');
        });
        break;
    }
  }, [
    resolution,
    returnDonationId,
    returnSessionId,
    storedRedirectSession,
    currency.code,
    resolvedRedirectSession,
  ]);

  // localStorage recovery effect — when returning from a native banking app that killed the
  // browser tab, there are no URL params and no redirectResult. We bypass the normal resolution
  // hook entirely and check the donation status directly via redirectContext.
  const localStorageRecoveryRef = useRef(false);
  useEffect(() => {
    if (!isLocalStorageRecovery || !returnDonationId || !redirectContext) return;
    if (localStorageRecoveryRef.current) return;
    localStorageRecoveryRef.current = true;

    const status = redirectContext.status;
    const isSettled = (SETTLED_DONATION_STATUSES as readonly string[]).includes(status);

    queueMicrotask(() => {
      trackDonation('donation_redirect_resumed', {
        donation_id: returnDonationId,
        webhook_settled: isSettled,
        has_stored_session: false,
      });

      if (status === 'paid') {
        clearInFlightPayment();
        trackDonation('donation_payment_completed', {
          result_code: 'Authorised',
          donation_id: returnDonationId,
          payment_method: 'redirect',
          event_source: 'webhook_settled',
        });
        trackDonation('donation_completed', {
          donation_id: returnDonationId,
          amount: 0,
          currency: currency.code,
          event_source: 'webhook_settled',
        });
        onRedirectSuccessRef.current?.();
        setCurrentStep('success');
      } else if (isSettled) {
        clearInFlightPayment();
        trackDonation('donation_redirect_failed', {
          donation_id: returnDonationId,
          reason: status,
        });
        setCurrentStep('failed');
      } else {
        // Still pending — poll for webhook settlement
        setCurrentStep('awaiting-settlement');
      }
    });
  }, [isLocalStorageRecovery, returnDonationId, redirectContext, currency.code]);

  // Query error handling — if payment config fails during finalization,
  // move to failed step instead of leaving the spinner stuck
  useEffect(() => {
    if (currentStep !== 'finalizing' && currentStep !== 'awaiting-settlement') return;
    if (isPaymentConfigError) {
      trackDonation('donation_error', {
        step: 'finalization',
        error_type: 'context_load_failed',
        error_message: 'payment_config_error',
      });
      queueMicrotask(() => setCurrentStep('failed'));
    }
  }, [currentStep, isPaymentConfigError]);

  // SDK finalization callbacks
  const handleFinalizationCompleted = useCallback(
    (data: PaymentCompletedData) => {
      if (data.resultCode === 'Authorised') {
        trackDonation('donation_payment_completed', {
          result_code: data.resultCode,
          donation_id: returnDonationId ?? undefined,
          payment_method: 'redirect',
          event_source: 'redirect_finalized',
        });
        trackDonation('donation_completed', {
          donation_id: returnDonationId ?? undefined,
          amount: redirectDisplayAmount?.amount ?? 0,
          currency: currency.code,
          event_source: 'redirect_finalized',
        });
        onRedirectSuccessRef.current?.();
        setCurrentStep('success');
      } else if (
        data.resultCode === 'Received' ||
        data.resultCode === 'Pending' ||
        data.resultCode === 'PresentToShopper'
      ) {
        trackDonation('donation_payment_completed', {
          result_code: data.resultCode,
          donation_id: returnDonationId ?? undefined,
          payment_method: 'redirect',
          event_source: 'redirect_finalized',
        });
        trackDonation('donation_completed', {
          donation_id: returnDonationId ?? undefined,
          amount: redirectDisplayAmount?.amount ?? 0,
          currency: currency.code,
          event_source: 'redirect_finalized',
        });
        setRedirect((prev) => ({ ...prev, optimisticSuccess: true }));
        onRedirectSuccessRef.current?.();
        setCurrentStep('success');
      } else {
        // Refused or other non-success
        trackDonation('donation_redirect_failed', {
          donation_id: returnDonationId ?? undefined,
          reason: data.resultCode,
        });
        if (returnDonationId) {
          markRedirectFailed.mutate({
            donationId: returnDonationId,
            reason: `Redirect finalization: ${data.resultCode}`,
          });
        }
        setCurrentStep('failed');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markRedirectFailed is stable (tRPC mutation)
    [returnDonationId, redirectDisplayAmount, currency.code],
  );

  const handleFinalizationFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      trackDonation('donation_redirect_failed', {
        donation_id: returnDonationId ?? undefined,
        reason: data?.resultCode || 'finalization_failed',
      });
      if (returnDonationId) {
        markRedirectFailed.mutate({
          donationId: returnDonationId,
          reason: data?.resultCode
            ? `Redirect failed: ${data.resultCode}`
            : 'Redirect finalization failed',
        });
      }
      setCurrentStep('failed');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markRedirectFailed is stable (tRPC mutation)
    [returnDonationId],
  );

  // Calculate actual donation amount
  const actualAmount = amount.custom ? parseFloat(amount.custom) || 0 : amount.selected || 0;

  // Platform fee computation
  const platformFeeAmount = feeConfig
    ? Math.round(Math.min(actualAmount * feeConfig.feeRate, feeConfig.capAmount) * 100) / 100
    : 0;
  const totalCharge =
    includePlatformFee && platformFeeAmount > 0 ? actualAmount + platformFeeAmount : actualAmount;

  // Format currency — locale-aware symbol placement and grouping
  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: amt % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amt);

  // Stripe configs leave the modal for /donate/checkout — prefetch the route
  // so "Continue to Payment" lands there without a loading gap.
  const isStripeProvider = paymentConfigData?.paymentConfigs?.[0]?.cardPaymentProvider === 'stripe';
  useEffect(() => {
    if (open && isStripeProvider) {
      router.prefetch(buildLocalePath(locale as Locale, '/donate/checkout'));
    }
  }, [open, isStripeProvider, router, locale]);

  // Derive Adyen checkout config from payment config
  const adyenConfig = paymentConfigData?.paymentConfigs?.[0];
  const checkoutConfig = adyenConfig
    ? {
        locale,
        clientKey: adyenConfig.adyenClientKey || '',
        countryCode: donorCountryCode,
        adyenLive: adyenConfig.adyenLive ?? false,
        applePay: adyenConfig.adyenApplePay ?? false,
        googlePay: adyenConfig.adyenGooglePay ?? false,
        ideal: adyenConfig.adyenIdeal ?? false,
        bancontact: adyenConfig.adyenBancontact ?? false,
        sepa: adyenConfig.adyenSepa ?? false,
        twint: adyenConfig.adyenTwint ?? false,
        eps: adyenConfig.adyenEps ?? false,
        multibanco: adyenConfig.adyenMultibanco ?? false,
        paypal: adyenConfig.adyenPaypal ?? false,
      }
    : null;
  // Validate details step
  const isEmailValid = isDonorEmailValid(effectiveDonorEmail);
  const { data: addressStateLookups = [] } = trpc.publicLookups.statesByCountry.useQuery(
    { countryCode: address.fields.country },
    { enabled: !!address.fields.country },
  );
  const missingAddressFields = useMemo(
    () =>
      getMissingAddressFields({
        required: true,
        streetAddress: address.fields.streetAddress,
        city: address.fields.city,
        state: address.fields.state,
        postalCode: address.fields.postalCode,
        country: address.fields.country,
        stateRequired: addressStateLookups.length > 0,
      }),
    [
      address.fields.city,
      address.fields.country,
      address.fields.postalCode,
      address.fields.state,
      address.fields.streetAddress,
      addressStateLookups.length,
    ],
  );
  const isAddressValid = missingAddressFields.length === 0;
  const isNameValid =
    donor.donationType === 'individual'
      ? donor.firstName.trim().length > 0 && donor.lastName.trim().length > 0
      : donor.companyName.trim().length > 0;
  const hasInvalidEmail = normalizedDonorEmail.length > 0 && !isEmailValid;
  const isEmailMissing = normalizedDonorEmail.length === 0;
  const missingDetailLabels = useMemo(() => {
    const labels: string[] = [];

    if (!isNameValid) {
      labels.push(donor.donationType === 'organization' ? tForm('companyName') : tForm('yourName'));
    }
    if (isEmailMissing || hasInvalidEmail) {
      labels.push(tForm('email'));
    }
    const addressFieldLabels: Record<AddressValidationField, string> = {
      streetAddress: tForm('streetAddress'),
      city: tForm('city'),
      state: tForm('state'),
      postalCode: tForm('postalCode'),
      country: tForm('country'),
    };
    missingAddressFields.forEach((field) => labels.push(addressFieldLabels[field]));

    return labels;
  }, [
    donor.donationType,
    hasInvalidEmail,
    isEmailMissing,
    isNameValid,
    missingAddressFields,
    tForm,
  ]);
  const shouldShowDetailsValidation = showDetailsValidation && missingDetailLabels.length > 0;
  const isDetailsValid = actualAmount > 0 && isEmailValid && isAddressValid && isNameValid;
  useScrollIntoViewOnReveal(
    detailsValidationRef,
    currentStep === 'details' && shouldShowDetailsValidation,
    {
      enabled: isMobile,
      block: 'center',
      focus: true,
    },
  );

  // Gift Aid visibility
  const showGiftAid =
    currency.code === 'GBP' && (paymentConfigData?.paymentConfigs?.[0]?.giftAidEnabled ?? false);
  const isSessionCreationPending = createCheckoutSession.isPending;

  // Handle continue to payment — creates checkout session then transitions
  const handleContinueToPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowDetailsValidation(true);

    if (isEmailMissing || !isEmailValid) {
      setPayment((prev) => ({
        ...prev,
        emailError: isEmailMissing ? tForm('enterEmailToContinue') : tForm('invalidEmail'),
      }));
    } else if (payment.emailError) {
      setPayment((prev) => ({ ...prev, emailError: null }));
    }
    if (!isDetailsValid) {
      trackDonation(
        'donation_validation_shown',
        { missing_fields: missingDetailLabels, missing_count: missingDetailLabels.length },
        analyticsCtx,
      );
      return;
    }
    if (!checkoutConfig) {
      setPayment((prev) => ({
        ...prev,
        error:
          isPaymentConfigLoading || isPaymentConfigFetching
            ? tForm('errorPaymentOptionsLoading')
            : tForm('errorCardPaymentsUnavailable'),
      }));
      return;
    }

    trackDonation('donation_details_submitted', {
      amount: actualAmount,
      currency: currency.code,
      is_anonymous: donor.anonymous,
      donation_type: donor.donationType,
      gift_aid: donor.giftAidEligible,
      has_platform_fee: includePlatformFee && platformFeeAmount > 0,
    });

    // Stripe configs hand off to the dedicated checkout page — the Payment
    // Element mounts there, not in the modal. Adyen stays in-modal below.
    if (isStripeProvider) {
      const { firstName, lastName } = resolveDonorName(donor, normalizedDonorEmail);
      const key = saveStripeCheckoutPayload({
        scope: effectiveCheckoutScope,
        amount: totalCharge,
        baseAmount: actualAmount,
        platformFee: includePlatformFee ? platformFeeAmount : 0,
        currency: { code: currency.code, symbol: currency.symbol },
        donor: {
          donorEmail: normalizedDonorEmail,
          donorFirstName: firstName,
          donorLastName: lastName,
          donorCompanyName: donor.companyName || undefined,
          donorDisplayName: donor.displayName || undefined,
          message: donor.message || undefined,
          isAnonymous: donor.anonymous,
          giftAidEligible: donor.giftAidEligible,
          donorTaxNumber: donor.taxNumber || undefined,
        },
        donorCountryCode,
        donorAddress: {
          streetAddress: address.fields.streetAddress,
          city: address.fields.city,
          state: address.fields.state || undefined,
          postalCode: address.fields.postalCode,
          country: address.fields.country || undefined,
          addressLine2: address.fields.addressLine2 || undefined,
        },
        recipientName,
        returnTo: window.location.pathname,
      });
      if (!key) {
        setPayment((prev) => ({ ...prev, error: tForm('errorGeneric') }));
        return;
      }

      trackDonation('donation_checkout_initiated', {
        amount: actualAmount,
        total_charge: totalCharge,
        currency: currency.code,
        gateway: 'stripe',
      });

      setPayment((prev) => ({ ...prev, error: null }));
      setCurrentStep('processing');
      router.push(buildLocalePath(locale as Locale, `/donate/checkout?k=${key}`));
      return;
    }

    setPayment((prev) => ({ ...prev, error: null, adyenReady: false }));
    setCurrentStep('processing');

    try {
      const { firstName, lastName } = resolveDonorName(donor, normalizedDonorEmail);

      // Build return URL for bank redirects — redirect back to this page
      const returnUrl = window.location.origin + window.location.pathname;

      // Resolve address
      const donorAddress = {
        streetAddress: address.fields.streetAddress,
        city: address.fields.city,
        state: address.fields.state || undefined,
        postalCode: address.fields.postalCode,
        country: address.fields.country || undefined,
        addressLine2: address.fields.addressLine2 || undefined,
      };

      const donorFields = {
        amount: totalCharge,
        platformFee: includePlatformFee ? platformFeeAmount : 0,
        donorEmail: normalizedDonorEmail,
        donorFirstName: firstName,
        donorLastName: lastName,
        donorCompanyName: donor.companyName || undefined,
        donorDisplayName: donor.displayName || undefined,
        message: donor.message || undefined,
        isAnonymous: donor.anonymous,
        giftAidEligible: donor.giftAidEligible,
        returnUrl,
        donorCountryCode,
        donorAddress,
        donorTaxNumber: donor.taxNumber || undefined,
      };

      const checkoutResult = await createCheckoutSession.mutateAsync({
        ...donorFields,
        scope: effectiveCheckoutScope,
      });

      // Save redirect session with commit inputs for bank redirect return
      saveAdyenRedirectSession({
        donationId: checkoutResult.donationId,
        sessionId: checkoutResult.sessionId,
        sessionData: checkoutResult.sessionData,
        donorEmail: normalizedDonorEmail,
        flow: 'donation',
        amount: actualAmount,
        currencySymbol: currency.symbol,
        commitInputs: {
          donorFirstName: firstName,
          donorLastName: lastName,
          donorCompanyName: donor.companyName || undefined,
          donorDisplayName: donor.displayName || undefined,
          message: donor.message || undefined,
          isAnonymous: donor.anonymous,
          giftAidEligible: donor.giftAidEligible,
          returnUrl,
          donorCountryCode,
          donorAddress,
          donorTaxNumber: donor.taxNumber || undefined,
          platformFee: includePlatformFee ? platformFeeAmount : 0,
          scope: effectiveCheckoutScope,
        },
      });

      // Persist minimal in-flight signal to localStorage (survives native app 3DS redirects
      // that kill the browser tab, where sessionStorage is wiped and no URL params exist)
      saveInFlightPayment(checkoutResult.donationId, checkoutResult.sessionId);

      trackDonation('donation_checkout_initiated', {
        amount: actualAmount,
        total_charge: totalCharge,
        currency: currency.code,
        donation_id: checkoutResult.donationId,
        has_paypal: !!checkoutResult.paypalAvailable,
      });

      setPayment((prev) => ({ ...prev, checkoutSession: checkoutResult }));
      setCurrentStep('payment');
    } catch (error) {
      const isCaptchaFailure = isTurnstileRejection(error);
      trackDonation('donation_error', {
        step: 'checkout',
        error_type: isCaptchaFailure ? 'captcha_rejected' : 'session_creation_failed',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      console.error('[DonationFormModal] Checkout session creation failed:', error);
      setPayment((prev) => ({
        ...prev,
        error: isCaptchaFailure ? tForm('errorCaptcha') : tForm('errorGeneric'),
      }));
      setCurrentStep('details');
    }
  };

  // Handle back to details
  const handleBackToDetails = () => {
    setCurrentStep('details');
    setPayment((prev) => ({
      ...prev,
      checkoutSession: null,
      error: null,
      adyenReady: false,
    }));
    setSelectedPaymentMethod('card');
  };

  // Handle back to amount selection
  const handleBackToAmount = () => {
    setCurrentStep('amount');
  };

  useEffect(() => {
    if (currentStep !== 'details' && showDetailsValidation) {
      setShowDetailsValidation(false);
    }
  }, [currentStep, showDetailsValidation]);

  // Track step views + reset step timer
  const userVisibleSteps = ['amount', 'details', 'payment'] as const;
  useEffect(() => {
    if (!open) return;
    if (!userVisibleSteps.includes(currentStep as (typeof userVisibleSteps)[number])) return;
    stepEnteredAtRef.current = Date.now();
    trackDonation(
      'donation_step_viewed',
      { step: currentStep, amount: actualAmount },
      analyticsCtx,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire on step change
  }, [currentStep, open]);

  // Adyen SDK lifecycle callbacks
  const handleAdyenReady = useCallback(() => {
    setPayment((prev) => ({ ...prev, adyenReady: true }));
  }, []);

  const handleAdyenError = useCallback(
    (error: Error) => {
      trackDonation('donation_error', {
        step: 'payment',
        error_type: 'adyen_init_failed',
        error_message: error.message,
      });
      console.error('[DonationFormModal] Adyen SDK initialization failed:', error);
      setPayment((prev) => ({
        ...prev,
        error: tForm('errorPaymentInitFailed'),
        checkoutSession: null,
        adyenReady: false,
      }));
      setCurrentStep('details');
    },
    [tForm],
  );

  const handlePaymentSuccess = useCallback(
    (opts?: { pendingReview?: boolean }) => {
      if (opts?.pendingReview) {
        setRedirect((prev) => ({ ...prev, optimisticSuccess: true }));
      }
      onSubmit?.({
        amount: actualAmount,
        name: donor.anonymous
          ? tCommon('anonymous')
          : `${donor.firstName} ${donor.lastName}`.trim(),
        email: normalizedDonorEmail,
        message: donor.message || undefined,
        anonymous: donor.anonymous,
        address: address.fields,
        donorTaxNumber: donor.taxNumber || undefined,
        donationId: payment.checkoutSession?.donationId ?? returnDonationId ?? undefined,
      });
      setCurrentStep('success');
    },
    [
      actualAmount,
      normalizedDonorEmail,
      donor,
      address,
      onSubmit,
      tCommon,
      payment.checkoutSession?.donationId,
      returnDonationId,
    ],
  );

  // Commit checkout on Adyen submit — creates the donation row + maps payment method
  const handleBeforeSubmit = useCallback(
    async (paymentMethodType: string) => {
      lastPaymentMethodRef.current = paymentMethodType;
      if (!payment.checkoutSession) throw new Error('No checkout session');

      const { firstName, lastName } = resolveDonorName(donor, normalizedDonorEmail);
      const returnUrl = window.location.origin + window.location.pathname;
      const donorAddress = {
        streetAddress: address.fields.streetAddress,
        city: address.fields.city,
        state: address.fields.state || undefined,
        postalCode: address.fields.postalCode,
        country: address.fields.country || undefined,
        addressLine2: address.fields.addressLine2 || undefined,
      };

      await commitCheckout.mutateAsync({
        amount: totalCharge,
        platformFee: includePlatformFee ? platformFeeAmount : 0,
        donorEmail: normalizedDonorEmail,
        donorFirstName: firstName,
        donorLastName: lastName,
        donorCompanyName: donor.companyName || undefined,
        donorDisplayName: donor.displayName || undefined,
        message: donor.message || undefined,
        isAnonymous: donor.anonymous,
        giftAidEligible: donor.giftAidEligible,
        returnUrl,
        donorCountryCode,
        donorAddress,
        donorTaxNumber: donor.taxNumber || undefined,
        scope: effectiveCheckoutScope,
        donationId: payment.checkoutSession.donationId,
        adyenSessionId: payment.checkoutSession.sessionId,
        adyenSessionData: payment.checkoutSession.sessionData,
        adyenMethodType: paymentMethodType,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commitCheckout is stable (tRPC mutation)
    [
      payment.checkoutSession,
      donor,
      normalizedDonorEmail,
      actualAmount,
      donorCountryCode,
      address.fields,
      effectiveCheckoutScope,
    ],
  );

  // Adyen payment callbacks
  const handlePaymentCompleted = useCallback(
    (data: PaymentCompletedData) => {
      clearInFlightPayment();

      if (
        data.resultCode === 'Authorised' ||
        data.resultCode === 'Received' ||
        data.resultCode === 'Pending'
      ) {
        trackDonation('donation_payment_completed', {
          result_code: data.resultCode,
          donation_id: payment.checkoutSession?.donationId,
          payment_method: lastPaymentMethodRef.current,
          event_source: 'checkout',
        });
        trackDonation('donation_completed', {
          donation_id: payment.checkoutSession?.donationId,
          amount: actualAmount,
          currency: currency.code,
          event_source: 'checkout',
        });
        handlePaymentSuccess();
        return;
      }

      // Voucher payments (Multibanco): keep Drop-in mounted so customer sees payment reference
      if (data.resultCode === 'PresentToShopper') {
        trackDonation('donation_payment_completed', {
          result_code: data.resultCode,
          donation_id: payment.checkoutSession?.donationId,
          payment_method: lastPaymentMethodRef.current,
          event_source: 'checkout',
        });
        trackDonation('donation_completed', {
          donation_id: payment.checkoutSession?.donationId,
          amount: actualAmount,
          currency: currency.code,
          event_source: 'checkout',
        });
        setCurrentStep('voucher-pending');
        return;
      }

      trackDonation('donation_payment_failed', {
        result_code: data.resultCode,
        donation_id: payment.checkoutSession?.donationId,
        payment_method: lastPaymentMethodRef.current,
      });
      setPayment((prev) => ({
        ...prev,
        error: tForm('errorPaymentNotCompleted', { resultCode: data.resultCode }),
        checkoutSession: null,
      }));
      setCurrentStep('details');
    },
    [handlePaymentSuccess, tForm, payment.checkoutSession?.donationId, actualAmount, currency.code],
  );

  const handlePaymentFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      clearInFlightPayment();
      trackDonation('donation_payment_failed', {
        result_code: data?.resultCode,
        donation_id: payment.checkoutSession?.donationId,
        payment_method: lastPaymentMethodRef.current,
      });
      const reason = data?.resultCode || 'Unknown error';
      setPayment((prev) => ({
        ...prev,
        error: tForm('errorPaymentFailed', { reason }),
        checkoutSession: null,
      }));
      setCurrentStep('details');
    },
    [tForm, payment.checkoutSession?.donationId],
  );

  const handlePayPalPaymentFailed = useCallback(
    (message: string, donationId?: string) => {
      trackDonation('donation_payment_failed', {
        donation_id: payment.checkoutSession?.donationId,
        payment_method: 'paypal',
      });
      const id = donationId ?? payment.checkoutSession?.donationId;
      if (id) {
        markRedirectFailed.mutate({ donationId: id, reason: `PayPal: ${message}` });
      }
      setPayment((prev) => ({ ...prev, error: message, checkoutSession: null }));
      setCurrentStep('details');
    },
    [markRedirectFailed, payment.checkoutSession?.donationId],
  );

  // Reset form
  const handleDonateAgain = () => {
    setCurrentStep('amount');
    setAmount(createInitialAmount(donationAmounts));
    setDonor(createInitialDonor());
    setAddress(createInitialAddress());
    setPayment(createInitialPayment());
    setIncludePlatformFee(true);
    setRedirect((prev) => ({ ...prev, finalizing: null }));
    prefillApplied.current = false;
    receiptPollStartRef.current = null;
  };

  // Handle closing modal
  const handleCloseModal = () => {
    clearInFlightPayment();
    // Track abandonment (skip success — that's a completion, not an abandonment)
    if (currentStep !== 'success') {
      trackDonation(
        'donation_modal_closed',
        {
          step: currentStep,
          amount: actualAmount || undefined,
          time_in_step_ms: Date.now() - stepEnteredAtRef.current,
        },
        analyticsCtx,
      );
    }
    if (currentStep === 'payment') {
      const donationId = paypalDonationIdRef.current ?? payment.checkoutSession?.donationId;
      if (donationId) {
        markRedirectFailed.mutate({
          donationId,
          reason: 'Modal closed during payment',
        });
      }
      paypalDonationIdRef.current = null;
    }
    if (
      currentStep === 'success' ||
      currentStep === 'failed' ||
      currentStep === 'finalizing' ||
      currentStep === 'awaiting-settlement'
    ) {
      setRedirect((prev) => ({ ...prev, optimisticSuccess: false }));
      handleDonateAgain();
    }
    onOpenChange(false);
  };

  // Radix may call onOpenChange with both true/false; only run close cleanup on false.
  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    handleCloseModal();
  };

  // Handle continue from amount to details
  const handleContinueFromAmount = () => {
    if (actualAmount > 0) {
      trackDonation('donation_amount_selected', {
        amount: actualAmount,
        is_custom: !!amount.custom,
        currency: currency.code,
      });
      setCurrentStep('details');
    }
  };

  const handleAmountSelect = (amt: number) => {
    if (amt === 0) {
      setAmount((prev) => ({ ...prev, selected: null }));
    } else {
      setAmount((prev) => ({ ...prev, selected: amt }));
    }
  };

  // Render modal content based on step
  const renderModalContent = () => {
    // Success state
    if (currentStep === 'success') {
      // For redirect returns, use amount from sessionStorage (form state is lost on reload);
      // for normal payments, use form actualAmount.
      // DB fallback via redirectContext when sessionStorage is unavailable (Safari ITP, cross-tab).
      const displayAmountStr = redirectDisplayAmount
        ? `${redirectDisplayAmount.currencySymbol || currency.symbol}${redirectDisplayAmount.amount.toLocaleString()}`
        : redirectContext?.sourceAmount
          ? new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: redirectContext.currencyCode || currency.code,
              minimumFractionDigits: Number(redirectContext.sourceAmount) % 1 === 0 ? 0 : 2,
              maximumFractionDigits: 2,
            }).format(Number(redirectContext.sourceAmount))
          : formatCurrency(actualAmount);

      return (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 animate-in fade-in zoom-in-50 duration-300">
            <Check className="w-8 h-8 text-green-500 animate-in fade-in zoom-in-0 duration-300 delay-150" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2 animate-in fade-in slide-in-from-bottom-2 duration-300 delay-200">
            {tForm('thankYou')}
          </h2>
          <p className="text-stone-400 mb-2 animate-in fade-in duration-300 delay-300">
            {redirect.optimisticSuccess
              ? tForm('pendingMessage', { amount: displayAmountStr })
              : tForm('confirmationMessage', { amount: displayAmountStr })}
          </p>
          <p className="text-stone-500 text-sm mb-6">
            {redirect.optimisticSuccess
              ? tForm('pendingEmail', { email: normalizedDonorEmail })
              : tForm('confirmationEmail', { email: normalizedDonorEmail })}
          </p>
          <div className="flex gap-3 justify-center">
            {receiptStatus?.taxReceiptAvailable && tenantSlug && successDonationId && (
              <Button
                variant="outline"
                className="border-stone-600 bg-stone-900 text-stone-300 hover:bg-stone-800"
                onClick={() =>
                  window.open(
                    `/api/receipts/${tenantSlug}/donations/${successDonationId}`,
                    '_blank',
                  )
                }
              >
                <FileDown className="w-4 h-4 mr-2" />
                {tForm('downloadReceipt')}
              </Button>
            )}
            <Button
              onClick={() => {
                setRedirect((prev) => ({ ...prev, optimisticSuccess: false }));
                handleDonateAgain();
                onOpenChange(false);
              }}
              className="bg-dgr-gold hover:bg-dgr-gold-hover text-stone-950"
            >
              {tCommon('done')}
            </Button>
          </div>
        </div>
      );
    }

    // Finalizing state — SDK redirect finalization after bank redirect return
    if (currentStep === 'finalizing') {
      return (
        <div className="text-center py-6">
          {finalizingTimedOut ? (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {tForm('finalizingTimedOutTitle')}
              </h2>
              <p className="text-stone-400 mb-6">{tForm('finalizingTimedOutMessage')}</p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  className="border-stone-700 text-stone-700"
                  onClick={dismissFinalizingTimeout}
                >
                  {tForm('keepWaiting')}
                </Button>
                <Button
                  variant="outline"
                  className="border-stone-700 text-stone-700"
                  onClick={handleCloseModal}
                >
                  {tCommon('close')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 text-dgr-gold animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">
                {tForm('finalizingPayment')}
              </h2>
              <p className="text-stone-400">{tForm('finalizingMessage')}</p>
              {normalizedDonorEmail ? (
                <p className="text-stone-500 text-sm mt-2">
                  {tForm('finalizingEmail', { email: normalizedDonorEmail })}
                </p>
              ) : null}
            </>
          )}
          {/* Hidden Adyen SDK for redirect finalization */}
          {finalizingState && checkoutConfig ? (
            <div className="hidden">
              <AdyenCheckout
                session={{
                  id: finalizingState.sessionId,
                  sessionData: finalizingState.sessionData,
                }}
                config={checkoutConfig}
                redirectResult={finalizingState.redirectResult}
                onPaymentCompleted={handleFinalizationCompleted}
                onPaymentFailed={handleFinalizationFailed}
                onError={() => setCurrentStep('failed')}
              />
            </div>
          ) : null}
        </div>
      );
    }

    // Awaiting settlement — bank redirect payment processing, polling for webhook settlement
    if (currentStep === 'awaiting-settlement') {
      return (
        <div className="text-center py-6">
          {settlement.timedOut ? (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {tForm('finalizingTimedOutTitle')}
              </h2>
              <p className="text-stone-400 mb-2">{tForm('finalizingTimedOutMessage')}</p>
              {normalizedDonorEmail ? (
                <p className="text-stone-400 mb-6">
                  {tForm('settlementTimeoutSafeToClose', { email: normalizedDonorEmail })}
                </p>
              ) : null}
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  className="border-stone-700 text-stone-700"
                  onClick={handleCloseModal}
                >
                  {tCommon('close')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 text-dgr-gold animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">
                {tForm('awaitingSettlement')}
              </h2>
              <p className="text-stone-400">{tForm('awaitingSettlementMessage')}</p>
              {normalizedDonorEmail ? (
                <p className="text-stone-500 text-sm mt-2">
                  {tForm('finalizingEmail', { email: normalizedDonorEmail })}
                </p>
              ) : null}
            </>
          )}
        </div>
      );
    }

    // Failed state — payment failed or cancelled, offer retry
    if (currentStep === 'failed') {
      return (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">{tForm('paymentFailed')}</h2>
          <p className="text-stone-400 mb-6">{tForm('paymentFailedMessage')}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => {
                setRedirect((prev) => ({ ...prev, finalizing: null }));
                setCurrentStep('details');
                setPayment((prev) => ({
                  ...prev,
                  checkoutSession: null,
                  error: null,
                }));
              }}
              className="bg-dgr-gold hover:bg-dgr-gold-hover text-stone-950"
            >
              {tForm('tryAgain')}
            </Button>
            <Button
              variant="outline"
              className="border-stone-700 text-stone-700"
              onClick={handleCloseModal}
            >
              {tCommon('close')}
            </Button>
          </div>
        </div>
      );
    }

    // Processing state
    if (currentStep === 'processing') {
      return (
        <div className="text-center py-10">
          <Loader2 className="w-12 h-12 text-dgr-gold animate-spin mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">{tForm('preparingCheckout')}</h2>
          <p className="text-stone-400 text-sm">{tForm('preparingCheckoutMessage')}</p>
        </div>
      );
    }

    // Payment step
    if (currentStep === 'payment' || currentStep === 'voucher-pending') {
      const paypalAvailable =
        !!payment.checkoutSession?.paypalAvailable && !!payment.checkoutSession?.paypalClientId;

      return (
        <>
          {/* Header with back button — hidden during voucher-pending */}
          {currentStep !== 'voucher-pending' ? (
            <div className="flex items-center gap-3 mb-4">
              <button
                type="button"
                onClick={handleBackToDetails}
                className="p-2 -ml-2 rounded-lg hover:bg-stone-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-stone-400" />
              </button>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-dgr-gold" />
                <h2 className="text-lg font-semibold text-white">{tForm('payment')}</h2>
              </div>
            </div>
          ) : null}

          {/* Order summary — mobile only (sidebar handles desktop) */}
          <div className="bg-stone-800/50 rounded-lg p-4 mb-4 sm:hidden">
            <div className="flex justify-between items-center">
              <span className="text-stone-400 text-sm">
                {tForm('donationTo', { recipientName })}
              </span>
              <span className="text-white font-semibold">{formatCurrency(actualAmount)}</span>
            </div>
            {includePlatformFee && platformFeeAmount > 0 && (
              <>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-stone-500 text-xs">{tForm('platformFeeLabel')}</span>
                  <span className="text-stone-400 text-sm">
                    {formatCurrency(platformFeeAmount)}
                  </span>
                </div>
                <div className="border-t border-stone-700 mt-2 pt-2 flex justify-between items-center">
                  <span className="text-stone-300 text-sm font-medium">{tCommon('total')}</span>
                  <span className="text-white font-semibold">{formatCurrency(totalCharge)}</span>
                </div>
              </>
            )}
          </div>

          {/* Payment method tabs — hidden during voucher-pending */}
          {currentStep !== 'voucher-pending' && paypalAvailable && payment.adyenReady ? (
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setSelectedPaymentMethod('card')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                  selectedPaymentMethod === 'card'
                    ? 'border-dgr-gold bg-dgr-gold/10 text-white'
                    : 'border-stone-700 bg-stone-800/50 text-stone-400 hover:border-stone-600'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Card</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedPaymentMethod('paypal')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                  selectedPaymentMethod === 'paypal'
                    ? 'border-dgr-gold bg-dgr-gold/10 text-white'
                    : 'border-stone-700 bg-stone-800/50 text-stone-400 hover:border-stone-600'
                }`}
              >
                {/* Two-tone PayPal monogram. Inner subpaths combined with evenodd fill rule
                    create the hole counters in each P loop, matching the official PayPal mark. */}
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.23A.932.932 0 0 1 5.865 1.5h6.474c2.715 0 4.632.702 5.694 2.09.462.604.763 1.262.917 2.01.163.79.198 1.741.099 2.906l-.013.148v.413l.322.182a3.99 3.99 0 0 1 1.003.756c.421.478.694 1.069.81 1.755.12.71.083 1.554-.105 2.505-.217 1.1-.568 2.058-1.046 2.847a5.525 5.525 0 0 1-1.655 1.755c-.646.425-1.4.733-2.242.918a11.96 11.96 0 0 1-2.74.29H12.59a1.24 1.24 0 0 0-1.222 1.04l-.017.107-.332 2.103-.013.083a1.24 1.24 0 0 1-1.222 1.04H7.076ZM10.7 4.7l-.95 5.6h2.55c1.55 0 2.55-.95 2.8-2.55.13-.95-.05-1.7-.55-2.2-.5-.55-1.3-.85-2.35-.85h-1.5z" />
                  <path
                    d="M18.282 7.876v.31c-.93 4.766-4.12 6.413-8.192 6.413H8.037a1.007 1.007 0 0 0-.995.852l-1.04 6.602-.295 1.87a.53.53 0 0 0 .523.614h3.647a.881.881 0 0 0 .87-.743l.036-.186.69-4.375.044-.24a.881.881 0 0 1 .871-.744h.548c3.547 0 6.325-1.44 7.136-5.608.339-1.74.163-3.193-.732-4.215a3.51 3.51 0 0 0-1.007-.755l-.051-.025ZM11.7 11.05l-.85 5.4h2.4c1.45 0 2.45-.9 2.7-2.45.13-.9-.05-1.6-.55-2.1-.5-.5-1.25-.85-2.25-.85h-1.45z"
                    opacity=".7"
                  />
                </svg>
                <span>PayPal</span>
              </button>
            </div>
          ) : null}

          {/* Card payment (Adyen Drop-in) */}
          {selectedPaymentMethod === 'card' && payment.checkoutSession && checkoutConfig ? (
            <div className="space-y-4">
              {!payment.adyenReady && (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full bg-stone-800" />
                  <Skeleton className="h-10 w-full bg-stone-800" />
                  <Skeleton className="h-10 w-3/4 bg-stone-800" />
                  <Skeleton className="h-12 w-full bg-stone-800" />
                </div>
              )}
              <div className={payment.adyenReady ? undefined : 'sr-only'}>
                <AdyenCheckout
                  session={{
                    id: payment.checkoutSession.sessionId,
                    sessionData: payment.checkoutSession.sessionData,
                  }}
                  config={checkoutConfig}
                  onPaymentCompleted={handlePaymentCompleted}
                  onPaymentFailed={handlePaymentFailed}
                  onBeforeSubmit={handleBeforeSubmit}
                  onReady={handleAdyenReady}
                  onError={handleAdyenError}
                  theme={resolvedTheme}
                />
              </div>

              {currentStep === 'voucher-pending' ? (
                <div className="mt-4 rounded-lg border border-stone-700 bg-stone-800/50 p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 shrink-0 text-dgr-gold mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-white">{tForm('voucherPendingTitle')}</p>
                      <p className="text-sm text-stone-400">
                        {tForm('voucherPendingDonationMessage')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-stone-600 text-xs">
                  <Lock className="w-3 h-3" />
                  <span>{tForm('sslSecured')}</span>
                </div>
              )}
            </div>
          ) : null}

          {/* PayPal payment */}
          {selectedPaymentMethod === 'paypal' && paypalAvailable ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-stone-700 bg-stone-800/50 p-6 text-center space-y-4">
                <svg className="w-20 h-6 mx-auto" viewBox="0 0 100 26" fill="none">
                  <path
                    d="M12.237 3.5H5.502a.833.833 0 0 0-.823.703L2.072 21.84a.5.5 0 0 0 .494.577h3.213a.833.833 0 0 0 .823-.703l.703-4.457a.833.833 0 0 1 .823-.703h1.897c3.952 0 6.234-1.914 6.83-5.708.268-1.66.011-2.963-.766-3.876C15.258 4.058 13.947 3.5 12.237 3.5Z"
                    fill="#253B80"
                  />
                  <path
                    d="M13.705 9.132c-.322 2.116-1.937 2.116-3.5 2.116h-.889l.623-3.948a.5.5 0 0 1 .494-.422h.408c1.064 0 2.069 0 2.588.606.31.362.404.9.276 1.648Z"
                    fill="#253B80"
                  />
                  <path
                    d="M27.37 9.066h-3.22a.5.5 0 0 0-.494.422l-.13.818-.205-.297c-.635-.922-2.05-1.23-3.462-1.23-3.238 0-6.003 2.452-6.54 5.893-.28 1.717.117 3.358 1.09 4.502.893 1.051 2.168 1.49 3.687 1.49 2.606 0 4.051-1.675 4.051-1.675l-.131.813a.5.5 0 0 0 .494.578h2.902a.833.833 0 0 0 .823-.703l1.74-11.033a.5.5 0 0 0-.494-.578h-.111Zm-4.079 5.706c-.282 1.67-1.607 2.793-3.3 2.793-.849 0-1.528-.273-1.964-.79-.432-.513-.595-1.244-.458-2.057.263-1.656 1.612-2.814 3.28-2.814.832 0 1.505.277 1.95.8.448.528.624 1.264.492 2.068Z"
                    fill="#253B80"
                  />
                  <path
                    d="M40.67 9.066h-3.233a.833.833 0 0 0-.688.366l-3.976 5.855-1.685-5.628a.833.833 0 0 0-.799-.593H27.1a.5.5 0 0 0-.474.664l3.174 9.315-2.986 4.214a.5.5 0 0 0 .408.789h3.23a.833.833 0 0 0 .684-.358l9.59-13.847a.5.5 0 0 0-.41-.777h.354Z"
                    fill="#253B80"
                  />
                  <path
                    d="M49.994 3.5h-6.735a.833.833 0 0 0-.823.703l-2.607 16.637a.5.5 0 0 0 .494.577h3.467a.583.583 0 0 0 .576-.492l.74-4.685a.833.833 0 0 1 .823-.703h1.897c3.952 0 6.234-1.914 6.83-5.708.268-1.66.011-2.963-.766-3.876C52.994 4.058 51.704 3.5 49.994 3.5Z"
                    fill="#179BD7"
                  />
                  <path
                    d="M51.462 9.132c-.322 2.116-1.937 2.116-3.5 2.116h-.889l.623-3.948a.5.5 0 0 1 .494-.422h.408c1.064 0 2.069 0 2.588.606.31.362.404.9.276 1.648Z"
                    fill="#179BD7"
                  />
                  <path
                    d="M65.127 9.066h-3.22a.5.5 0 0 0-.494.422l-.13.818-.205-.297c-.635-.922-2.05-1.23-3.462-1.23-3.238 0-6.003 2.452-6.54 5.893-.28 1.717.117 3.358 1.09 4.502.893 1.051 2.168 1.49 3.687 1.49 2.606 0 4.051-1.675 4.051-1.675l-.131.813a.5.5 0 0 0 .494.578h2.902a.833.833 0 0 0 .823-.703l1.74-11.033a.5.5 0 0 0-.494-.578h-.111Zm-4.079 5.706c-.282 1.67-1.607 2.793-3.3 2.793-.849 0-1.528-.273-1.964-.79-.432-.513-.595-1.244-.458-2.057.263-1.656 1.612-2.814 3.28-2.814.832 0 1.505.277 1.95.8.448.528.624 1.264.492 2.068Z"
                    fill="#179BD7"
                  />
                  <path
                    d="M67.875 3.81l-2.645 16.832a.5.5 0 0 0 .494.577h2.778a.833.833 0 0 0 .823-.703L72.04 3.879a.5.5 0 0 0-.494-.578h-3.133a.5.5 0 0 0-.494.422l-.044.087Z"
                    fill="#179BD7"
                  />
                </svg>
                <p className="text-stone-400 text-sm">
                  You will be redirected to PayPal to complete your {formatCurrency(actualAmount)}{' '}
                  donation.
                </p>
              </div>
              <PayPalCheckout
                clientId={payment.checkoutSession!.paypalClientId!}
                currencyCode={currency.code}
                createOrder={async () => {
                  const { firstName, lastName } = resolveDonorName(donor, normalizedDonorEmail);
                  const session = await strategy.createSession({
                    amount: totalCharge,
                    platformFee: includePlatformFee ? platformFeeAmount : 0,
                    donorEmail: normalizedDonorEmail,
                    donorFirstName: firstName,
                    donorLastName: lastName,
                    message: donor.message || undefined,
                    isAnonymous: donor.anonymous,
                    giftAidEligible: donor.giftAidEligible,
                    paymentMethod: 'paypal',
                    returnUrl: window.location.origin + window.location.pathname,
                    donorCountryCode,
                    selfAttributionEntrantId: selfAttributeEnabled
                      ? strategy.selfAttribution?.entrantId
                      : undefined,
                    donorAddress: {
                      streetAddress: address.fields.streetAddress,
                      city: address.fields.city,
                      state: address.fields.state || undefined,
                      postalCode: address.fields.postalCode,
                      country: address.fields.country || undefined,
                    },
                    donorTaxNumber: donor.taxNumber || undefined,
                  });
                  if (session.paymentMethod !== 'paypal')
                    throw new Error('Expected PayPal session');
                  paypalDonationIdRef.current = session.donationId;
                  return { donationId: session.donationId, orderId: session.orderId };
                }}
                onCaptureOrder={async (params) => capturePayPalOrder.mutateAsync(params)}
                onPaymentCompleted={(opts) => {
                  trackDonation('donation_payment_completed', {
                    result_code: 'Authorised',
                    donation_id: payment.checkoutSession?.donationId,
                    payment_method: 'paypal',
                    event_source: 'paypal',
                  });
                  trackDonation('donation_completed', {
                    donation_id: payment.checkoutSession?.donationId,
                    amount: actualAmount,
                    currency: currency.code,
                    event_source: 'paypal',
                  });
                  handlePaymentSuccess(opts);
                }}
                onPaymentFailed={handlePayPalPaymentFailed}
              />
              <div className="flex items-center justify-center gap-2 text-stone-600 text-xs">
                <Lock className="w-3 h-3" />
                <span>{tForm('sslSecured')}</span>
              </div>
            </div>
          ) : null}

          {!payment.checkoutSession ? (
            <div className="text-center py-6">
              <Loader2 className="w-8 h-8 text-dgr-gold animate-spin mx-auto mb-2" />
              <p className="text-stone-400 text-sm">{tForm('loadingPaymentMethods')}</p>
            </div>
          ) : null}

          {/* Error message */}
          {payment.error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mt-4">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-400 text-sm">{payment.error}</p>
            </div>
          )}
        </>
      );
    }

    // Amount step (full modal flow)
    if (currentStep === 'amount') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-dgr-gold" />
            <h2 className="text-lg font-semibold text-white">
              {tForm('supportRecipient', { recipientName })}
            </h2>
          </div>
          {/* Currency Notice */}
          <p className="text-stone-500 text-sm">
            {tForm('currencyNotice', { currency: `${currency.label} (${currency.code})` })}
          </p>

          <AmountSelector
            currency={currency}
            amounts={donationAmounts}
            selectedAmount={amount.selected}
            customAmount={amount.custom}
            onAmountSelect={handleAmountSelect}
            onCustomAmountChange={(v) => setAmount((prev) => ({ ...prev, custom: v }))}
          />

          {/* Continue Button */}
          <Button
            type="button"
            onClick={handleContinueFromAmount}
            disabled={actualAmount <= 0}
            className="w-full h-12 bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {actualAmount > 0
              ? tForm('continueWithAmount', { amount: formatCurrency(actualAmount) })
              : ''}
          </Button>

          {impactMessage ? (
            <p className="text-center text-stone-600 text-xs">{impactMessage}</p>
          ) : null}
        </div>
      );
    }

    // Details step (default in modal)
    return (
      <form noValidate onSubmit={handleContinueToPayment} className="space-y-3">
        {/* Back button + Amount Summary */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBackToAmount}
            className="p-2 -ml-2 rounded-lg hover:bg-stone-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-stone-400" />
          </button>
          <span className="text-stone-400 text-sm">{tForm('yourDetails')}</span>
          <span className="ml-auto text-white font-semibold sm:hidden">
            {formatCurrency(actualAmount)}
          </span>
        </div>

        {/* Self-attribution toggle */}
        {strategy.selfAttribution && (
          <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-dgr-gold" />
                <span className="text-white text-sm font-medium">
                  {tForm('selfAttribution.label')}
                </span>
              </div>
              <Switch
                checked={selfAttributeEnabled}
                onCheckedChange={setSelfAttributeEnabled}
                className="data-[state=checked]:bg-dgr-gold data-[state=unchecked]:bg-stone-600"
              />
            </div>
            <p className="text-stone-400 text-xs mt-2 hidden sm:block">
              {selfAttributeEnabled
                ? tForm('selfAttribution.enabledHint')
                : tForm('selfAttribution.disabledHint')}
            </p>
          </div>
        )}

        {/* Donor Info */}
        <div className="space-y-3">
          {/* Name + Anonymous */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-stone-300 text-sm">
                {donor.donationType === 'organization' ? tForm('companyName') : tForm('yourName')}
              </Label>
              <label
                htmlFor="modal-donation-anonymous"
                className="flex items-center gap-1.5 text-stone-400 text-xs cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="modal-donation-anonymous"
                  checked={donor.anonymous}
                  onChange={(e) => updateDonor('anonymous', e.target.checked)}
                  className="rounded border-stone-600 bg-stone-800 accent-primary focus:ring-primary w-4 h-4"
                />
                {tForm('donateAnonymously')}
              </label>
            </div>
            {donor.donationType === 'organization' ? (
              <Input
                value={donor.companyName}
                onChange={(e) => updateDonor('companyName', e.target.value)}
                className={cn(
                  'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500',
                  shouldShowDetailsValidation &&
                    !donor.companyName.trim() &&
                    'border-red-500 focus-visible:ring-red-500',
                )}
                placeholder={tForm('companyNamePlaceholder')}
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={donor.firstName}
                  onChange={(e) => updateDonor('firstName', e.target.value)}
                  className={cn(
                    'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500',
                    shouldShowDetailsValidation &&
                      !donor.firstName.trim() &&
                      'border-red-500 focus-visible:ring-red-500',
                  )}
                  placeholder={tForm('firstNamePlaceholder')}
                />
                <Input
                  value={donor.lastName}
                  onChange={(e) => updateDonor('lastName', e.target.value)}
                  className={cn(
                    'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500',
                    shouldShowDetailsValidation &&
                      !donor.lastName.trim() &&
                      'border-red-500 focus-visible:ring-red-500',
                  )}
                  placeholder={tForm('lastNamePlaceholder')}
                />
              </div>
            )}

            {/* Org/Individual toggle — below name fields for context */}
            <button
              type="button"
              onClick={() =>
                updateDonor(
                  'donationType',
                  donor.donationType === 'organization' ? 'individual' : 'organization',
                )
              }
              className="text-stone-400 text-xs hover:text-stone-300 transition-colors"
            >
              {donor.donationType === 'organization'
                ? `← ${tForm('yourName')}`
                : tForm('donateAsOrg')}
            </button>
          </div>

          {/* Display Name (optional — collapsed by default) */}
          {showDisplayName || donor.displayName ? (
            <div className="space-y-1.5">
              <Label className="text-stone-300 text-sm">{tForm('displayName')}</Label>
              <Input
                value={donor.displayName}
                onChange={(e) => updateDonor('displayName', e.target.value)}
                className="h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                placeholder={tForm('displayNamePlaceholder')}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDisplayName(true)}
              className="text-stone-400 text-xs hover:text-stone-300 transition-colors"
            >
              + {tForm('displayName')}
            </button>
          )}

          {/* Email */}
          <div className="space-y-1.5">
            <Label className="text-stone-300 text-sm">{tForm('email')}</Label>
            <Input
              type="email"
              value={effectiveDonorEmail}
              onChange={(e) => {
                updateDonor('email', e.target.value);
                if (payment.emailError) updatePayment('emailError', null);
              }}
              className={cn(
                'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500',
                (payment.emailError ||
                  (shouldShowDetailsValidation && (isEmailMissing || hasInvalidEmail))) &&
                  'border-red-500 focus-visible:ring-red-500',
              )}
              placeholder={tForm('emailPlaceholder')}
            />
            {payment.emailError && <p className="text-red-400 text-xs">{payment.emailError}</p>}
          </div>

          {/* Address */}
          <DonorAddressFields
            address={address.fields}
            taxNumber={donor.taxNumber}
            countries={allCountries}
            googlePlacesApiKey={paymentConfigData?.googlePlacesApiKey}
            requiredFields={shouldShowDetailsValidation ? missingAddressFields : []}
            onAddressChange={updateAddressField}
            onTaxNumberChange={(value) => updateDonor('taxNumber', value)}
            onCountryChange={(code) => {
              updateAddressField('country', code);
              updateAddressField('state', '');
              if (!TAX_NUMBER_COUNTRIES.has(code)) {
                setDonor((prev) => ({ ...prev, taxNumber: '' }));
              }
            }}
          />

          {/* Gift Aid */}
          {showGiftAid && (
            <GiftAidSection
              checked={donor.giftAidEligible}
              onChange={(checked) => updateDonor('giftAidEligible', checked)}
              amount={formatCurrency(actualAmount * 0.25)}
            />
          )}

          {/* Message */}
          <div className="space-y-1.5">
            <Label className="text-stone-300 text-sm">{tForm('messageOptional')}</Label>
            <Textarea
              value={donor.message}
              onChange={(e) => updateDonor('message', e.target.value)}
              className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 resize-none text-sm"
              placeholder={tForm('messagePlaceholder')}
              rows={1}
            />
          </div>
        </div>

        {/* Platform fee opt-in */}
        {feeConfig && platformFeeAmount > 0 && (
          <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="platform-fee"
                checked={includePlatformFee}
                onChange={(e) => setIncludePlatformFee(e.target.checked)}
                className="rounded border-stone-600 bg-stone-800 accent-primary focus:ring-primary w-4 h-4 mt-0.5"
              />
              <label htmlFor="platform-fee" className="cursor-pointer">
                <span className="text-white text-sm font-medium block">
                  {tForm('platformFeeLabel')}
                </span>
                <span className="text-stone-400 text-xs">
                  {tForm('platformFeeDescription', {
                    amount: formatCurrency(platformFeeAmount),
                  })}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Sticky CTA footer across breakpoints so the button stays visible on tall forms (mobile, iPad, desktop). */}
        <div className="sticky bottom-0 z-10 -mx-5 border-t border-stone-800 bg-stone-900/95 backdrop-blur-sm px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-3">
          {/* Error message */}
          {payment.error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-400 text-sm">{payment.error}</p>
            </div>
          )}

          {/* Validation summary */}
          {shouldShowDetailsValidation ? (
            <div
              ref={detailsValidationRef}
              tabIndex={-1}
              role="alert"
              aria-live="assertive"
              className="rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-2.5"
            >
              <div className="flex items-start gap-2 text-xs text-red-300">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-400 mt-0.5" />
                <div>
                  <span>{tForm('detailsRequiredTitle')}</span>
                  <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-1">
                    {missingDetailLabels.map((label, i) => (
                      <span key={label} className="text-red-400">
                        {label}
                        {i < missingDetailLabels.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Continue to Payment Button — uses semantic `primary` so each tenant's
              brand accent flows through (DGR gold, DGD gold, UWR orange, etc.). */}
          <Button
            type="submit"
            disabled={isSessionCreationPending}
            className="w-full h-12 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {isSessionCreationPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {tForm('preparingCheckoutButton')}
              </>
            ) : (
              tForm('continueToPayment')
            )}
          </Button>

          {isPaymentConfigError ? (
            <p className="text-center text-red-400 text-xs">
              {tForm('donationsUnavailableProfile')}
            </p>
          ) : null}
        </div>

        {impactMessage ? (
          <p className="text-center text-stone-600 text-xs hidden sm:block">{impactMessage}</p>
        ) : null}
      </form>
    );
  };

  // Steps that show the donation summary sidebar on desktop
  const showSummaryPanel =
    currentStep === 'details' || currentStep === 'payment' || currentStep === 'voucher-pending';

  // Only render the Dialog — no trigger rendering
  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        data-theme-light={themeLightAttr}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className={cn(
          'bg-stone-900 text-white p-0 gap-0 overflow-hidden',
          // Mobile: bottom sheet, auto-sized to content with cap
          'bottom-0 left-0 right-0 top-auto max-w-none max-h-[90dvh] rounded-t-xl rounded-b-none border-0',
          'translate-x-0 translate-y-0',
          // Desktop: centered modal
          'sm:inset-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:rounded-lg',
          'sm:translate-x-[-50%] sm:translate-y-[-50%]',
          'sm:border sm:border-stone-800',
          // Animations
          'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
          'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:zoom-out-95',
          // Width
          showSummaryPanel ? 'sm:max-w-3xl' : 'sm:max-w-md',
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Heart className="w-5 h-5 text-dgr-gold" />
            {tForm('supportRecipient', { recipientName })}
          </DialogTitle>
        </DialogHeader>

        {showSummaryPanel ? (
          <div className="overflow-y-auto max-h-[90dvh] sm:h-auto sm:overflow-hidden sm:grid sm:grid-cols-[1fr_280px] sm:max-h-[85vh]">
            {/* Form content — scrollable. sm:max-h cap is required so sm:overflow-y-auto actually
                activates a scroll container; grid rows would otherwise size to the form's natural
                height, leaving sticky bottom positioning with no scroll context to attach to. */}
            <div className="min-h-0 p-5 sm:max-h-[85vh] sm:overflow-y-auto">
              {renderModalContent()}
            </div>

            {/* Summary sidebar — desktop only, right side */}
            <aside className="hidden sm:flex flex-col justify-between min-h-0 bg-stone-950 p-6 border-l border-stone-800">
              <div className="tabular-nums">
                <Heart className="w-6 h-6 text-dgr-gold mb-4" />
                <p className="text-stone-400 text-sm mb-1">
                  {tForm('donationTo', { recipientName })}
                </p>
                <div className="text-3xl font-bold text-white tracking-tight mb-6 transition-all duration-200">
                  {formatCurrency(actualAmount)}
                </div>

                <div className="space-y-2.5 text-sm">
                  {includePlatformFee && platformFeeAmount > 0 ? (
                    <>
                      <div className="flex justify-between gap-2">
                        <span className="text-stone-500">{tForm('platformFeeLabel')}</span>
                        <span className="text-stone-400 whitespace-nowrap transition-opacity duration-200">
                          {formatCurrency(platformFeeAmount)}
                        </span>
                      </div>
                      <div className="border-t border-stone-800 pt-2.5 flex justify-between gap-2">
                        <span className="text-stone-300 font-medium">{tCommon('total')}</span>
                        <span className="text-white font-semibold text-lg whitespace-nowrap transition-all duration-200">
                          {formatCurrency(totalCharge)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="border-t border-stone-800 pt-2.5 flex justify-between gap-2">
                      <span className="text-stone-300 font-medium">{tCommon('total')}</span>
                      <span className="text-white font-semibold text-lg whitespace-nowrap">
                        {formatCurrency(actualAmount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-stone-600 text-xs mt-6">
                <Lock className="w-3 h-3" />
                <span>{tForm('sslSecured')}</span>
              </div>
            </aside>
          </div>
        ) : (
          <div className="overflow-y-auto p-5 max-h-[90dvh] sm:h-auto sm:max-h-[85vh]">
            {renderModalContent()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
